import React, { useCallback, useEffect, useState } from "react"

import { Loader2, Search } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import DashboardLayout from "@/layouts/DashboardLayout"
import { supabase } from "@/lib/supabase"

const StickyLocations: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [rows, setRows] = useState<any[]>([])
  const [search, setSearch] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [syncRunning, setSyncRunning] = useState(false)
  const [syncMessage, setSyncMessage] = useState<string | null>(null)
  const [activeFilter, setActiveFilter] = useState<"all" | "active" | "inactive">("all")

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      let query = supabase
        .from("sticky_locations")
        .select("id, code, latitude, longitude, availability, airtable_record_id, active")

      if (search.trim()) {
        const s = `%${search}%`
        query = query.or(
          `code.ilike.${s},latitude.ilike.${s},longitude.ilike.${s},availability.ilike.${s},airtable_record_id.ilike.${s}`,
        )
      }

      // Apply active filter
      if (activeFilter === "active") {
        query = query.eq("active", true)
      } else if (activeFilter === "inactive") {
        query = query.eq("active", false)
      }
      // "all" means no filter

      const { data, error } = await query.order("active", { ascending: false }).order("code")
      if (error) throw error

      setRows(data ?? [])
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [search, activeFilter])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const runSync = async () => {
    if (syncRunning) return
    setSyncRunning(true)
    setSyncMessage(null)

    try {
      await supabase.functions.invoke("airtable-sync-sticky-locations")
      setSyncMessage("Sync completed")
      await fetchData()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setSyncMessage(`Sync failed: ${e.message}`)
    } finally {
      setSyncRunning(false)
    }
  }

  const toggleActive = async (id: string, currentActive: boolean) => {
    try {
      // Optimistic update
      setRows((prevRows) =>
        prevRows.map((row) => (row.id === id ? { ...row, active: !currentActive } : row)),
      )

      const { error } = await supabase
        .from("sticky_locations")
        .update({ active: !currentActive })
        .eq("id", id)

      if (error) {
        // Revert on error
        setRows((prevRows) =>
          prevRows.map((row) => (row.id === id ? { ...row, active: currentActive } : row)),
        )
        throw error
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setError(`Failed to toggle active: ${e.message}`)
    }
  }

  return (
    <DashboardLayout>
      <div className="max-w-[95vw] mx-auto p-4">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Sticky Locations</h1>
          <p className="text-muted-foreground">
            Browse Sticky Locations. Sync to fetch latest from Airtable.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardDescription>Showing: {rows.length}</CardDescription>

            <div className="mt-3 flex gap-3 items-center flex-wrap">
              <div className="relative w-64">
                <Search className="absolute left-2 top-2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full rounded-md border pl-8 pr-2 py-1 bg-background"
                />
              </div>

              <Select
                value={activeFilter}
                onValueChange={(value: "all" | "active" | "inactive") => setActiveFilter(value)}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active Only</SelectItem>
                  <SelectItem value="inactive">Inactive Only</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>

              <Button onClick={runSync} disabled={syncRunning}>
                {syncRunning ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Syncing…
                  </span>
                ) : (
                  "Sync from Airtable"
                )}
              </Button>

              {syncMessage && <div className="text-sm">{syncMessage}</div>}
            </div>
          </CardHeader>

          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : error ? (
              <div className="text-red-600">{error}</div>
            ) : (
              <div className="w-full overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Latitude</TableHead>
                      <TableHead>Longitude</TableHead>
                      <TableHead>Availability</TableHead>
                      <TableHead>Airtable Record ID</TableHead>
                      <TableHead>Active</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((row, idx) => (
                      <TableRow key={idx} className={row.active ? "" : "opacity-50"}>
                        <TableCell>{row.code}</TableCell>
                        <TableCell>{row.latitude ?? "-"}</TableCell>
                        <TableCell>{row.longitude ?? "-"}</TableCell>
                        <TableCell>{row.availability ?? "-"}</TableCell>
                        <TableCell>{row.airtable_record_id}</TableCell>
                        <TableCell>
                          <Switch
                            checked={Boolean(row.active)}
                            onCheckedChange={() =>
                              toggleActive(String(row.id), Boolean(row.active))
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}

export default StickyLocations
